import { useMutation, useQuery } from '@apollo/client/react';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_USER_PROFILES_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { UPDATE_USER_PROFILE_MUTATION } from '../../integrations/allkort/graphql/gql/UserProfileMutations';
import { BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { UpdateUserProfileResponse } from '../../integrations/allkort/graphql/interface/UserProfile';

/**
 * useUpdateUserProfile
 *
 * Custom hook for updating an existing user profile's personal information.
 * Fetches the current customer's ID from cache automatically.
 *
 * @example
 * ```tsx
 * const { updateUserProfile, loading, error } = useUpdateUserProfile();
 *
 * await updateUserProfile({
 *   userProfileId: 285507,
 *   email: 'new@email.com',
 *   firstName: 'Anna',
 *   identifier: '199001011234',
 *   lastName: 'Svensson',
 *   phoneNumber: '+46701234567',
 * });
 * ```
 */
export const useUpdateUserProfile = () => {
	const { data: customerData } = useQuery<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		{ fetchPolicy: 'cache-first' }
	);

	const [updateUserProfileMutation, { loading, error, data }] =
		useMutation<UpdateUserProfileResponse>(UPDATE_USER_PROFILE_MUTATION, {
			refetchQueries: [{ query: GET_USER_PROFILES_QUERY }],
			awaitRefetchQueries: true,
		});

	const updateUserProfile = async (input: {
		userProfileId: number
		email: string
		firstName: string
		lastName: string
		phoneNumber: string
	}) => {
		const customerId = customerData?.currentCustomer?.id;
		if (!customerId) {
			throw new Error('Customer ID not available');
		}

		try {
			const result = await updateUserProfileMutation({
				variables: {
					customerId,
					userProfileId: input.userProfileId,
					email: input.email,
					firstName: input.firstName,
					lastName: input.lastName,
					phoneNumber: input.phoneNumber,
				},
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useUpdateUserProfile] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.updateUserProfile?.userProfile ?? null;
		} catch (err) {
			throw err;
		}
	};

	return { updateUserProfile, loading, error, data };
};
